import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import AnimatedSection from '../components/ui/AnimatedSection';
import { Star, Briefcase } from 'react-feather';
import FunnelHero from '../components/funnel/FunnelHero';
import AuthorityBurst from '../components/funnel/AuthorityBurst';
import MechanismDiagram from '../components/funnel/MechanismDiagram';
import OfferStack from '../components/funnel/OfferStack';
import CTABand from '../components/funnel/CTABand';
import QuickCallbackForm from '../components/funnel/QuickCallbackForm';
import EpiphanyBridgeContent from '../components/funnel/EpiphanyBridgeContent';
import FaqSection from '../components/blog/FaqSection';
import Seo from '../components/shared/Seo';
import { generateFaqSchema, generatePracticeAreaSchema, generateHowToSchema, generateBreadcrumbSchema } from '../lib/schema';
import NotFoundPage from './NotFoundPage';
import FlippableCard from '../components/ui/FlippableCard';
import StatewideServiceBanner from '../components/shared/StatewideServiceBanner';
import Breadcrumbs from '../components/shared/Breadcrumbs';
import OtherPracticeAreas from '../components/practice-areas/OtherPracticeAreas';
import { useData } from '../context/DataContext';
import LocalizedLink from '../components/ui/LocalizedLink';
import { useTranslation } from 'react-i18next';

const PracticeAreaDetailPage: React.FC = () => {
  const { t } = useTranslation('common');
  const { slug: rawSlug } = useParams<{ slug: string }>();
  const { firmStats, getPracticeAreaContentBySlug } = useData();
  
  // SEO FIX: Clean the slug to prevent issues with trailing slashes, which can cause broken link errors for crawlers.
  const slug = useMemo(() => rawSlug?.replace(/\/$/, ''), [rawSlug]);
  
  // All redirect logic has been moved to the server-side _redirects file.
  // This component will now only receive clean slugs.
  const practiceArea = slug ? getPracticeAreaContentBySlug(slug) : undefined;

  if (!practiceArea) {
    return <NotFoundPage />;
  }

  const faqs = practiceArea.faqs || [];
  const funnelHeadline = practiceArea.funnelHeadline || practiceArea.title;
  const funnelSubheadline = practiceArea.funnelSubheadline || practiceArea.subtitle;
  const epiphanyBridgeContent = practiceArea.epiphanyBridgeContent || [];
  const mechanism = practiceArea.mechanism || { title: '', subtitle: '', steps: [] };
  const offerStack = practiceArea.offerStack || [];
  const relevantTestimonial = practiceArea.relevantTestimonial || { quote: '', author: '' };
  const relevantCaseResult = practiceArea.relevantCaseResult || { amount: '', caseType: '' };
  const riskReversal = practiceArea.riskReversal || { title: '', description: '' };
  
  const breadcrumbs = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.practiceAreas'), path: '/practice-areas' },
    { name: practiceArea.title, path: `/practice-areas/${practiceArea.slug}` }
  ];

  // --- DEFINITIVE SCHEMA FIX ---
  // Step 1: Generate the main LegalService schema for this page.
  const practiceAreaSchema = generatePracticeAreaSchema(practiceArea);

  // Step 2 (Review schema) was removed here -- Site Integration Audit item
  // 4: it built a self-authored Review with itemReviewed pointing back at
  // this page's own LegalService, exactly the pattern Google's own
  // review-snippet guidelines say is ineligible for the star-rating rich
  // result. See lib/schema.ts's own comment for the full reasoning.

  // Step 3: Generate all other necessary schemas for the page.
  const howToSchema = generateHowToSchema(practiceArea);
  const faqSchema = faqs.length > 0 ? generateFaqSchema(faqs) : null;
  const breadcrumbSchema = generateBreadcrumbSchema(breadcrumbs);

  // Step 4: Combine all schemas into a single array for the @graph.
  const combinedSchema = [
      practiceAreaSchema,
      howToSchema,
      faqSchema,
      breadcrumbSchema
  ].filter(Boolean);


  return (
    <>
      <Seo
        title={t('practiceAreaDetailPage.seoTitle', { practiceArea: practiceArea.title })}
        description={practiceArea.subtitle}
        schema={combinedSchema}
      />
      <div>
        <FunnelHero 
          headline={funnelHeadline}
          subheadline={funnelSubheadline}
          imageUrl={practiceArea.imageUrl}
        />

        <Breadcrumbs crumbs={breadcrumbs} />

        <AuthorityBurst stats={firmStats} />

        <div className="py-24 bg-brand-dark">
          <AnimatedSection>
              <EpiphanyBridgeContent content={epiphanyBridgeContent} />
          </AnimatedSection>
        </div>

        <MechanismDiagram mechanism={mechanism} />
        
        <OfferStack offerStack={offerStack} />

        <CTABand
          headline={t('practiceAreaDetail.ctaBandHeadline')}
          subheadline={t('practiceAreaDetail.ctaBandSubheadline')}
          buttonText={t('practiceAreaDetail.ctaBandButton')}
          buttonLink="/contact"
          isPrimary={true}
        />

        <div className="px-6 pb-24">
          <QuickCallbackForm practiceAreaSlug={practiceArea.slug} />
        </div>

        <AnimatedSection className="py-24">
          <div className="mx-auto max-w-7xl px-6">
             <div className="mx-auto mb-16 max-w-3xl text-center">
                 <h2 className="text-4xl md:text-5xl font-serif font-bold">{t('practiceAreaDetail.proofTitle', { practiceAreaTitle: practiceArea.title })}</h2>
                 <p className="mt-4 text-lg text-white/70">{t('practiceAreaDetail.proofSubtitle')}</p>
             </div>
             <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-stretch">
                <FlippableCard
                    frontContent={
                        <div className="flex h-full flex-col justify-center rounded-3xl border border-white/10 bg-brand-dark p-8 shadow-xl glass-effect">
                            <h3 className="text-2xl font-serif font-semibold text-center mb-4">{t('practiceAreaDetail.proofTestimonialTitle')}</h3>
                            <p className="text-xl leading-relaxed text-white/90 italic text-center">“{relevantTestimonial?.quote || 'Excellent service and results.'}”</p>
                        </div>
                    }
                    backContent={
                         <div className="flex h-full flex-col items-center justify-center rounded-3xl border border-white/10 bg-black/80 p-8 shadow-xl glass-effect text-center">
                           <div className="text-3xl font-bold gold-gradient-text">{relevantCaseResult?.amount || 'Confidential'}</div>
                           <div className="mt-2 text-md text-white/80">{relevantCaseResult?.caseType || 'Successful Settlement'}</div>
                        </div>
                    }
                />

                <FlippableCard
                    frontContent={
                        <div className="h-full rounded-3xl border border-yellow-400/20 bg-gradient-to-br from-yellow-400/10 to-transparent p-8 text-white shadow-card shadow-yellow-500/10 flex flex-col justify-center items-center text-center">
                            <h3 className="text-2xl font-serif font-semibold text-center mb-4">{t('practiceAreaDetail.proofResultTitle')}</h3>
                            <p className="text-lg tracking-wide text-white/80 text-center">{relevantCaseResult?.caseType || 'Successful Settlement'}</p>
                            <div className="text-6xl font-extrabold gold-gradient-text mt-2 text-center">{relevantCaseResult?.amount || 'Confidential'}</div>
                        </div>
                    }
                    backContent={
                        <div className="h-full rounded-3xl border border-yellow-400/20 bg-gradient-to-tr from-yellow-400/20 to-brand-dark/90 p-8 text-white shadow-card shadow-yellow-500/10 flex flex-col justify-center items-center text-center">
                             {relevantCaseResult?.practiceAreaSlug && (
                                <LocalizedLink to={`/practice-areas/${relevantCaseResult.practiceAreaSlug}`} className="inline-flex items-center gap-2 text-sm font-semibold text-white/90 transition-colors rounded-full px-4 py-2 bg-white/10 border border-white/10 hover:bg-white/20 glass-effect">
                                    <Briefcase size={16} /> {t('practiceAreaDetail.proofSeeMethodButton')}
                                </LocalizedLink>
                            )}
                        </div>
                    }
                />
             </div>
              <div className="mt-12 text-center">
                  <p className="text-xs text-white/50 italic">
                      {t('practiceAreaDetail.proofDisclaimer')}
                  </p>
              </div>
          </div>
        </AnimatedSection>
        
         <div className="py-24 bg-white/5">
            <FaqSection 
              faqs={faqs}
              title={t('practiceAreaDetail.faqTitle', { practiceAreaTitle: practiceArea.title })}
            />
         </div>

         <AnimatedSection className="py-24">
           <div className="mx-auto max-w-4xl px-6 text-center">
              <h2 className="text-4xl md:text-5xl font-serif font-bold">{riskReversal.title}</h2>
              <p className="mt-6 text-lg text-white/80">{riskReversal.description}</p>
           </div>
         </AnimatedSection>

        <StatewideServiceBanner practiceAreaTitle={practiceArea.title} />
        
        <OtherPracticeAreas currentSlug={practiceArea.slug} />

        <CTABand
          headline={t('practiceAreaDetail.finalCtaHeadline')}
          subheadline={t('practiceAreaDetail.finalCtaSubheadline')}
          buttonText={t('practiceAreaDetail.finalCtaButton')}
          buttonLink="/contact"
          isPrimary={true}
        />
      </div>
    </>
  );
};

export default PracticeAreaDetailPage;